<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import {
  fetcherEvents,
  showServiceNotification,
  resolveServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { toast } from "@/shared/store/alertStore";
import { useAuthedImage } from "@/shared/lib/composables/useAuthedImage";

import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import {
  getServiceListWithResponseByCode,
  runChainService,
  AUTO_FILLED_KEYS,
} from "@/entities/template";
import { getServiceErrorMessage } from "@/shared/lib/serviceError";
import { getKind, resolveServiceCode } from "@/entities/participant";

import FieldBoolean from "@/features/dynamic-fields/ui/FieldBoolean.vue";

import FieldString from "@/features/dynamic-fields/ui/FieldString.vue";

import FieldDecimal from "@/features/dynamic-fields/ui/FieldDecimal.vue";

import FieldInt from "@/features/dynamic-fields/ui/FieldInt.vue";

import FieldArray from "@/features/dynamic-fields/ui/FieldArray.vue";

import FieldDate from "@/features/dynamic-fields/ui/FieldDate.vue";

import FieldJson from "@/features/dynamic-fields/ui/FieldJson.vue";

import FieldEnum from "@/features/dynamic-fields/ui/FieldEnum.vue";

import Button from "@/shared/ui/button/Button.vue";
// TODO: Временно не используются (выбор резидента РК скрыт).
// import Radio from "@/shared/ui/radio/Radio.vue";
// import RadioGroup from "@/shared/ui/radio/RadioGroup.vue";
import { FormDialog } from "@/shared/ui/form-dialog";
import { Skeleton } from "@/shared/ui/skeleton";
import { Spinner } from "@/shared/ui/spinner";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";

const props = defineProps({
  description: String,
  participant_type: String,
  selectedRole: Object,
  execution: Object,
  participant: {
    type: Object,
    default: null,
  },
  form: {
    type: Object,
    default: () => ({}),
  },
});

const isEditMode = computed(() => Boolean(props.participant?.uuid));

const emit = defineEmits(["save", "close"]);

const { t } = useI18n();
const route = useRoute();
const digitalCallContext = inject("digitalCallContext", null);

// data
const visible = ref(false);
const isSearching = ref(false);
const searchCompleted = ref(false);
const isSchemaLoading = ref(true);
const canSearchInRegistry = ref(false);
const serviceForm = ref({});
const foundPerson = ref(null);
const { src: photoSrc, error: photoError } = useAuthedImage(
  () => foundPerson.value?.person_photo
);
const serviceSchema = ref(null);
const pendingRequestUuid = ref(null);
// { variant: "loading" | "error", title, description } | null
const serviceNotice = ref(null);
const NOTICE_ICONS = {
  error: { name: "sli-danger", color: "text-red-600" },
};
let unsubscribeWs = null;

function createParticipantForm() {
  let extra_keys = {};

  if (props.participant_type === "INDIVIDUAL") {
    extra_keys = {
      first_name: "",
      last_name: "",
      middle_name: "",
      person_photo: "",
    };
  }

  if (props.participant_type === "LEGAL") {
    extra_keys = {
      params: {
        company_name: "",
      },
    };
  }
  return {
    resident_kz: true,
    participant_type_code: props.selectedRole?.code || "",
    participant_type: props.participant_type || "INDIVIDUAL",
    identifier: "",
    ...extra_keys,
  };
}

const participantForm = ref(createParticipantForm());

// computed
const uuid = computed(() => props.form?.id ?? route.params.id);

const title = computed(() =>
  isEditMode.value ? t("Редактирование участника") : t("Добавление участника")
);

const isLegal = computed(
  () => participantForm.value.participant_type === "LEGAL"
);

const isIndividual = computed(
  () => participantForm.value.participant_type === "INDIVIDUAL"
);

const currentSteps = computed(
  () => props.execution?.steps_entity_types[props.participant_type]
);

const serviceCode = computed(() =>
  resolveServiceCode(getKind(participantForm.value), currentSteps.value || [])
);

const hasFoundPerson = computed(() => {
  if (participantForm.value.participant_type === "LEGAL") {
    return Boolean(foundPerson.value?.bin);
  }
  return Boolean(foundPerson.value?.iin);
});

const warningMessage = computed(() =>
  canSearchInRegistry.value
    ? null
    : t("У вас нет доступа к поиску данного типа лиц.")
);

const serviceFields = computed(() => {
  const request = serviceSchema.value?.request;
  if (!request?.properties) return [];

  const required = request.required || [];

  return Object.entries(request.properties)
    .map(([key, schema]) => ({
      key,
      label: schema.title ?? key,
      type: schema.type?.toLowerCase(),
      enums: schema.enum,
      required: required.includes(key),
      schema,
    }))
    .filter((f) => f.required);
});

const isSaveDisabled = computed(() => {
  if (participantForm.value.resident_kz) {
    return !hasFoundPerson.value;
  }

  const { first_name, last_name } = participantForm.value;
  return !(first_name || last_name);
});

// watch
watch(
  () => props.execution,
  () => {
    checkServiceAccess();
  },
  { deep: true }
);

watch(
  () => participantForm.value.participant_type,
  () => {
    reset();
    checkServiceAccess();
  }
);

// methods
function reset() {
  participantForm.value = createParticipantForm();
  serviceForm.value = {};
  foundPerson.value = null;
  searchCompleted.value = false;
}

function initServiceForm(schema = {}) {
  return Object.fromEntries(
    Object.entries(schema).map(([key, field]) => [
      key,
      field.default ?? (field.type === "boolean" ? false : ""),
    ])
  );
}

async function checkServiceAccess() {
  isSchemaLoading.value = true;
  const step = currentSteps.value?.find((s) => s.code === serviceCode.value);

  canSearchInRegistry.value = Boolean(step);

  if (canSearchInRegistry.value) {
    await loadServiceSchema();
  }
  isSchemaLoading.value = false;
}

async function loadServiceSchema() {
  const res = await getServiceListWithResponseByCode(serviceCode.value);
  if (!res?.success) return;

  serviceSchema.value = res.payload;
  serviceForm.value = initServiceForm(res.payload.request?.properties);

  if (isEditMode.value) {
    seedServiceFormFromParticipant();
  } else {
    seedServiceFormFromDigitalContext();
  }
}

// Ключи схемы, в которые подставляется идентификатор участника (ИИН/БИН).
// AUTO_FILLED_KEYS — канонические, остальные оставлены для legacy-сервисов.
const IDENTIFIER_FIELD_KEYS = [...AUTO_FILLED_KEYS, "identifier", "iin", "bin"];

function fillIdentifierFields(value) {
  for (const key of IDENTIFIER_FIELD_KEYS) {
    if (key in serviceForm.value) {
      serviceForm.value[key] = value;
    }
  }
}

function isIdentifierField(key) {
  return IDENTIFIER_FIELD_KEYS.includes(key);
}

function onIdentifierInput(key, event) {
  const value = event?.target ? event.target.value : event;
  serviceForm.value[key] = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 12);
}

function seedServiceFormFromParticipant() {
  const id = props.participant?.identifier;
  if (id) fillIdentifierFields(id);
}

function seedServiceFormFromDigitalContext() {
  const ctx = digitalCallContext?.value;
  if (!ctx || props.participant_type !== "INDIVIDUAL") return;

  const iinByRole = {
    DOVERITEL: ctx.principalIin,
    DOVERENNYI: ctx.attorneyIin,
  };
  const iin = iinByRole[props.selectedRole?.code];
  if (iin) fillIdentifierFields(iin);
}

async function searchPerson() {
  if (!(await validateAllForms())) return;

  cleanupWs();
  isSearching.value = true;
  searchCompleted.value = true;

  try {
    const step = currentSteps.value.find((s) => s.code === serviceCode.value);
    if (!step) {
      isSearching.value = false;
      return;
    }

    const clientRequestUuid = generateRequestUuid();
    const params = {
      ...serviceForm.value,
      request_uuid: clientRequestUuid,
    };

    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    pendingRequestUuid.value = clientRequestUuid;
    unsubscribeWs = fetcherEvents.on(handleWsResponse);

    const res = await runChainService(
      props.execution.uuid,
      step.uuid,
      uuid.value,
      params
    );

    if (!res?.success || !res.payload?.run) {
      foundPerson.value = null;
      toast.error(getServiceErrorMessage(res));
      cleanupWs();
      isSearching.value = false;
    }
  } catch (e) {
    console.error("searchPerson error:", e);
    toast.error(getServiceErrorMessage(e));
    cleanupWs();
    isSearching.value = false;
  }
}

function handleWsResponse(data) {
  if (data?.type !== "FETCHER") return;
  if (
    pendingRequestUuid.value &&
    data.request_uuid !== pendingRequestUuid.value
  ) {
    return;
  }

  const wsPayload = data?.payload;

  // PENDING — ещё обрабатывается, продолжаем ждать
  if (wsPayload?.status === "PENDING") return;

  // NOTIFICATION — ожидание подтверждения клиента (КДП), loading продолжается
  if (wsPayload?.status === "NOTIFICATION") {
    serviceNotice.value = {
      variant: "loading",
      ...resolveServiceNotification(wsPayload),
    };
    showServiceNotification(wsPayload);
    return;
  }

  // Cleanup подписки
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  if (
    wsPayload?.status === "FINISHED" &&
    wsPayload.data &&
    Object.keys(wsPayload.data).length > 0
  ) {
    serviceNotice.value = null;
    foundPerson.value = wsPayload.data;

    if (participantForm.value.participant_type === "INDIVIDUAL") {
      Object.assign(participantForm.value, {
        identifier: foundPerson.value.iin,
        first_name: foundPerson.value.first_name,
        last_name: foundPerson.value.last_name,
        middle_name: foundPerson.value.middle_name,
        person_photo: foundPerson.value.person_photo,
      });
    } else {
      Object.assign(participantForm.value, {
        identifier: foundPerson.value.bin,
        params: {
          company_name: foundPerson.value.name,
        },
      });
    }

    toast.success(t("Данные получены"), {
      description: new Date().toLocaleTimeString(),
      duration: 3000,
    });
  } else {
    foundPerson.value = null;

    if (wsPayload?.status === "ERROR" || wsPayload?.error_code) {
      const errorMsg =
        wsPayload?.error_description ||
        t("Не удалось получить данные от сервиса");
      serviceNotice.value = { variant: "error", description: errorMsg };
      toast.error(errorMsg, { duration: 5000 });
    } else {
      serviceNotice.value = null;
      toast.error(t("Не удалось получить данные от сервиса"), {
        duration: 5000,
      });
    }
  }

  isSearching.value = false;
}

function cleanupWs() {
  pendingRequestUuid.value = null;
  serviceNotice.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }
}

onBeforeUnmount(() => {
  cleanupWs();
});

async function validateAllForms() {
  const identifierKey = IDENTIFIER_FIELD_KEYS.find(
    (key) => key in serviceForm.value
  );
  const identifier = identifierKey
    ? String(serviceForm.value[identifierKey] ?? "")
    : "";

  const idLabel = isLegal.value ? t("БИН") : t("ИИН");

  if (identifier.length !== 12) {
    toast.warning(t("Поле должен содержать 12 цифр", { field: idLabel }));
    return false;
  }

  return true;
}

function save() {
  if (!hasFoundPerson.value && participantForm.value.resident_kz) return;

  emit("save", participantForm.value);
  close();
}

function close() {
  visible.value = false;
  emit("close");
}

function resolveFieldComponent(field) {
  if (field.enums?.length) return FieldEnum;

  return (
    {
      boolean: FieldBoolean,
      string: FieldString,
      number: FieldDecimal,
      integer: FieldInt,
      array: FieldArray,
      datetime: FieldDate,
      object: FieldJson,
    }[field.type] || FieldString
  );
}

function populateFromParticipant() {
  const p = props.participant;
  if (!p) return;

  participantForm.value = {
    resident_kz: p.resident_kz ?? true,
    participant_type_code: p.participant_type_code || "",
    participant_type:
      p.participant_type || props.participant_type || "INDIVIDUAL",
    identifier: p.identifier || "",
    ...(p.participant_type === "LEGAL"
      ? { params: { company_name: p.params?.company_name || "" } }
      : {
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          middle_name: p.middle_name || "",
          person_photo: p.person_photo || "",
        }),
  };

  if (p.participant_type === "LEGAL") {
    foundPerson.value = {
      bin: p.identifier,
      name: p.params?.company_name,
    };
  } else {
    foundPerson.value = {
      iin: p.identifier,
      first_name: p.first_name,
      last_name: p.last_name,
      middle_name: p.middle_name,
      person_photo: p.person_photo,
    };
  }
  searchCompleted.value = true;
}

// mounted
onMounted(() => {
  visible.value = true;
  if (isEditMode.value) {
    populateFromParticipant();
  } else {
    reset();
  }
  checkServiceAccess();
});
</script>

<template>
  <FormDialog
    v-model:open="visible"
    :title="title"
    :description="description"
    :width="500"
    @close="close"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <main class="flex flex-col gap-6 flex-1">
        <!-- TODO: Временно скрыт выбор резидента РК (по умолчанию "Да"). -->
        <!-- <div class="flex flex-col gap-2">
          <span class="text-muted-foreground text-xs">
            {{ $t("Резидент РК") }}
          </span>

          <RadioGroup
            v-model="participantForm.resident_kz"
            class="flex flex-row gap-4"
          >
            <Radio :value="true">{{ $t("Да") }}</Radio>
            <Radio :value="false">{{ $t("Нет") }}</Radio>
          </RadioGroup>
        </div> -->

        <template v-if="participantForm.resident_kz">
          <div v-if="isSchemaLoading" class="flex flex-col gap-3">
            <p class="text-muted-foreground text-sm">
              {{ $t("Загрузка данных...") }}
            </p>
            <Skeleton class="h-20 w-full" />
          </div>

          <div
            v-else-if="!canSearchInRegistry"
            class="flex gap-3 p-3 rounded-lg bg-yellow-100"
          >
            <svg-icon name="huge-warning" class="text-yellow-500" />
            <span>{{ warningMessage }}</span>
          </div>

          <template v-else>
            <div class="flex flex-col gap-1">
              <div class="flex flex-col">
                <FormItem v-for="field in serviceFields" :key="field.key" float>
                  <FormLabel v-if="field.type !== 'boolean'">
                    {{ field.label }}
                  </FormLabel>
                  <FormControl>
                    <Input
                      v-if="isIdentifierField(field.key)"
                      v-model="serviceForm[field.key]"
                      maxlength="12"
                      inputmode="numeric"
                      @input="onIdentifierInput(field.key, $event)"
                    />
                    <component
                      :is="resolveFieldComponent(field)"
                      v-else
                      v-model="serviceForm[field.key]"
                      :item="field"
                    />
                  </FormControl>
                </FormItem>
              </div>

              <div class="flex justify-end">
                <Button
                  @click="searchPerson"
                  class="w-full flex items-center justify-center"
                  :disabled="isSearching"
                  variant="outline"
                >
                  {{ $t("Запросить данные") }}
                </Button>
              </div>
            </div>

            <div class="flex flex-col gap-4">
              <div v-if="serviceNotice" class="flex items-start gap-2">
                <Spinner
                  v-if="serviceNotice.variant === 'loading'"
                  size="sm"
                  class="shrink-0 mt-1"
                />
                <svg-icon
                  v-else
                  :name="NOTICE_ICONS[serviceNotice.variant]?.name"
                  :class="NOTICE_ICONS[serviceNotice.variant]?.color"
                  class="shrink-0 mt-0.5 w-4 h-4"
                  style="fill: none"
                />
                <p
                  class="text-sm"
                  :class="
                    serviceNotice.variant === 'loading'
                      ? 'text-muted-foreground animate-pulse'
                      : 'text-foreground'
                  "
                >
                  {{
                    serviceNotice.description ||
                    $t("Ожидаем подтверждения от клиента...")
                  }}
                </p>
              </div>

              <template v-if="isSearching">
                <template v-if="!serviceNotice">
                  <p class="text-muted-foreground text-sm font-medium">
                    {{ $t("Идет поиск по базам данных...") }}
                  </p>
                  <Skeleton class="h-28 w-full" />
                </template>
              </template>

              <template
                v-else-if="
                  searchCompleted && serviceNotice?.variant !== 'error'
                "
              >
                <p class="text-muted-foreground text-sm font-medium">
                  {{
                    hasFoundPerson ? $t("Лицо найдено") : $t("Лицо не найдено")
                  }}
                </p>

                <div
                  v-if="hasFoundPerson"
                  class="p-4 rounded-2xl border border-border flex gap-4 items-center"
                >
                  <div v-if="photoSrc && !photoError">
                    <img
                      :src="photoSrc"
                      :alt="
                        foundPerson.last_name + ' ' + foundPerson.first_name
                      "
                      class="w-16 h-[78px] object-cover rounded-[var(--radius)] border border-border"
                      @error="photoError = true"
                    />
                  </div>
                  <div
                    v-else
                    class="w-16 h-[78px] rounded-[var(--radius)] border border-border flex items-center justify-center bg-muted"
                  >
                    <svg-icon
                      name="huge-user"
                      class="w-6 h-6 text-muted-foreground"
                    />
                  </div>
                  <div class="flex flex-col gap-2 flex-1">
                    <div v-if="isIndividual">
                      <p class="text-muted-foreground text-xs">
                        {{ $t("ФИО") }}
                      </p>
                      <p class="text-foreground text-sm">
                        {{ foundPerson.last_name }}
                        {{ foundPerson.first_name }}
                        {{ foundPerson.middle_name || "" }}
                      </p>
                    </div>

                    <div v-if="isLegal">
                      <p class="text-muted-foreground text-xs">
                        {{ $t("Организация") }}
                      </p>
                      <p class="text-foreground text-sm">
                        {{ foundPerson?.name || "" }}
                      </p>
                    </div>

                    <div>
                      <p class="text-muted-foreground text-xs">
                        {{ isIndividual ? $t("ИИН") : $t("БИН") }}
                      </p>
                      <p class="text-foreground text-sm">
                        {{ foundPerson.iin || foundPerson.bin }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </template>

        <template v-else>
          <div class="flex flex-col gap-4">
            <FormItem float>
              <FormLabel>{{ $t("Имя") }}</FormLabel>
              <FormControl>
                <Input v-model="participantForm.first_name" />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>{{ $t("Фамилия") }}</FormLabel>
              <FormControl>
                <Input v-model="participantForm.last_name" />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>{{ $t("Отчество") }}</FormLabel>
              <FormControl>
                <Input v-model="participantForm.middle_name" />
              </FormControl>
            </FormItem>
          </div>
        </template>
      </main>
    </div>

    <template #footer>
      <div class="flex-1"></div>
      <Button variant="outline" @click="close">
        {{ $t("Отмена") }}
      </Button>
      <Button
        class="ml-2"
        variant="default"
        :disabled="isSaveDisabled"
        @click="save"
      >
        {{ isEditMode ? $t("Сохранить") : $t("Добавить") }}
      </Button>
    </template>
  </FormDialog>
</template>
